import { AlertTriangle, Info, ShieldAlert, ShieldCheck } from 'lucide-react';
import { Explain } from '@/components/Explain';
import { cn } from '@/lib/cn';
import type { RiskResult, RiskSignal } from '@/types';

/**
 * Player risk — the built-in fraud signals.
 *
 * ⚠️ A HEURISTIC AID, never a verdict. Every signal is a reason to LOOK, scored
 * so the worst-looking players surface first. The header says so in plain words,
 * because a number labelled "risk" is very easy to read as an accusation.
 *
 * Reuses the bet-hints visual language (severity stripe + colour), so "something
 * is wrong here" looks the same everywhere in the console.
 */

const SEVERITY = {
  critical: { icon: ShieldAlert, cls: 'border-l-crit bg-crit-bg', text: 'text-crit', label: 'critical' },
  warning: { icon: AlertTriangle, cls: 'border-l-warn bg-warn-bg', text: 'text-warn', label: 'warning' },
  info: { icon: Info, cls: 'border-l-info bg-info-bg', text: 'text-info', label: 'info' },
} as const;

const LEVEL = {
  high: { text: 'text-crit', ring: 'border-crit/50', label: 'high' },
  elevated: { text: 'text-warn', ring: 'border-warn/50', label: 'elevated' },
  low: { text: 'text-info', ring: 'border-info/40', label: 'low' },
  none: { text: 'text-ok', ring: 'border-ok/40', label: 'clear' },
} as const;

const glossaryFor: Record<RiskSignal['key'], string> = {
  stake_regularity: 'stake_regularity',
  velocity: 'velocity',
  shared_ip: 'shared_ip',
  rollback_rate: 'rollback_rate',
  freebet_abuse: 'freebet_abuse',
  win_concentration: 'win_concentration',
};

export function RiskPanel({ risk }: { risk: RiskResult }) {
  const level = LEVEL[risk.level];

  return (
    <section className="border-b border-hairline">
      <div className="flex flex-wrap items-center gap-3 border-b border-hairline bg-surface px-4 py-2.5">
        <span className="font-condensed text-[12px] font-semibold uppercase tracking-wider text-text">
          <Explain term="risk_score">Risk</Explain>
        </span>

        <div className={cn('flex items-center gap-2 border px-2.5 py-1', level.ring)}>
          <span className={cn('tnum font-mono text-[17px] font-semibold', level.text)}>
            {risk.score}
          </span>
          <span className="font-condensed text-[10px] uppercase tracking-wider text-text-faint">
            / 100
          </span>
          <span className={cn('font-condensed text-[11px] font-semibold uppercase tracking-wider', level.text)}>
            {level.label}
          </span>
        </div>

        {/* Said out loud, not buried in a tooltip. */}
        <span className="text-[11.5px] text-text-faint">
          a heuristic aid for triage — not a verdict
        </span>
      </div>

      {risk.signals.length === 0 ? (
        <div className="flex items-center gap-2.5 px-4 py-4">
          <ShieldCheck size={16} className="shrink-0 text-ok" />
          <p className="text-[12.5px] text-text-dim">
            No risk signal fired for this player in this window. That is not proof of anything —
            only that nothing we check for stood out.
          </p>
        </div>
      ) : (
        <div className="space-y-px">
          {risk.signals.map((s) => {
            const meta = SEVERITY[s.severity];
            const Icon = meta.icon;
            return (
              <div key={s.key} className={cn('flex gap-3 border-l-2 px-4 py-2.5', meta.cls)}>
                <Icon size={15} className={cn('mt-[1px] shrink-0', meta.text)} />
                <div className="min-w-0">
                  <div className="flex items-baseline gap-2">
                    <span className="text-[13px] font-semibold text-text">
                      <Explain term={glossaryFor[s.key]}>{s.title}</Explain>
                    </span>
                    <span className={cn('font-condensed text-[10px] font-semibold uppercase tracking-wider', meta.text)}>
                      {meta.label}
                    </span>
                  </div>
                  <p className="mt-0.5 font-mono text-[11.5px] leading-relaxed text-text-dim">
                    {s.evidence}
                  </p>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </section>
  );
}
